import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { BarChart3, TrendingUp, Calendar, Building } from 'lucide-react';
import api from '../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Analytics: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('6months');

  const { data: tendersAnalytics, isLoading: tendersLoading } = useQuery(
    ['analytics', 'tenders', selectedPeriod],
    () => api.getAnalytics('tenders'),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: companiesAnalytics, isLoading: companiesLoading } = useQuery(
    ['analytics', 'companies'],
    () => api.getAnalytics('companies'),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: comuniStats, isLoading: comuniLoading } = useQuery(
    ['analytics', 'comuni'],
    () => api.getComuniStats(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const tendersChartData = {
    labels: Array.isArray(tendersAnalytics?.months) ? tendersAnalytics.months.map((m: any) => m.label) : [],
    datasets: [
      {
        label: 'Bandi Pubblicati',
        data: Array.isArray(tendersAnalytics?.months) ? tendersAnalytics.months.map((m: any) => m.count) : [],
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
    ],
  };

  const amountChartData = {
    labels: Array.isArray(tendersAnalytics?.months) ? tendersAnalytics.months.map((m: any) => m.label) : [],
    datasets: [
      {
        label: 'Valore Totale (€)',
        data: Array.isArray(tendersAnalytics?.months) ? tendersAnalytics.months.map((m: any) => m.total_amount) : [],
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 2,
        fill: true,
      },
    ],
  };

  const typeDistributionData = {
    labels: Array.isArray(tendersAnalytics?.types) ? tendersAnalytics.types.map((t: any) => t.tipo) : [],
    datasets: [
      {
        data: Array.isArray(tendersAnalytics?.types) ? tendersAnalytics.types.map((t: any) => t.count) : [],
        backgroundColor: [
          '#3B82F6',
          '#10B981',
          '#F59E0B',
          '#EF4444',
          '#8B5CF6',
          '#F97316',
          '#06B6D4',
          '#84CC16',
        ],
      },
    ],
  };

  const topCompaniesByAmount = companiesAnalytics?.top_by_amount?.slice(0, 10) || [];

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return '€' + value.toLocaleString();
          }
        }
      },
    },
  };

  if (tendersLoading || companiesLoading || comuniLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Analytics
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Analisi dettagliata dei dati sui bandi pubblici
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Bandi Totali
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {tendersAnalytics?.total_tenders?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Valore Totale
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    €{(tendersAnalytics?.total_amount || 0).toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Building className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Aziende Attive
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {companiesAnalytics?.total_companies?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Media Mensile
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {Math.round((tendersAnalytics?.total_tenders || 0) / 12)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tenders Timeline */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Andamento Bandi nel Tempo
          </h3>
          <div className="h-64">
            <Bar data={tendersChartData} options={chartOptions} />
          </div>
        </div>

        {/* Amount Timeline */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Valore Bandi nel Tempo
          </h3>
          <div className="h-64">
            <Line data={amountChartData} options={lineChartOptions} />
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Type Distribution */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Distribuzione per Tipo
          </h3>
          <div className="h-64">
            <Doughnut data={typeDistributionData} options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom' as const,
                },
              },
            }} />
          </div>
        </div>

        {/* Top Companies */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Top 10 Aziende per Valore
          </h3>
          <div className="space-y-3">
            {Array.isArray(topCompaniesByAmount) ? topCompaniesByAmount.map((company: any, index: number) => (
              <div key={company.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full text-sm font-medium">
                    {index + 1}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white truncate">
                    {company.nome}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  €{company.total_amount.toLocaleString()}
                </span>
              </div>
            )) : null}
          </div>
        </div>
      </div>

      {/* Comuni Statistics */}
      {comuniStats && comuniStats.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Statistiche per Comune
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.isArray(comuniStats) ? comuniStats.map((comune: any) => (
              <div key={comune.comune} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {comune.comune}
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Bandi:</span>
                    <span className="text-gray-900 dark:text-white">{comune.tender_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Valore:</span>
                    <span className="text-gray-900 dark:text-white">
                      €{comune.total_amount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;